import ChartCard from "@ui/Highcharts/ui/Card";
import React, { useMemo } from "react";
import { css, cx } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { FullViewportButtonMarker } from "@ui/FullViewportButton";
import { exportWidgetTarget } from "@ui/ExportWidgetButton";
import HighchartsComponent from "@ui/Highcharts";
import useOptions from "./useOptions";
import type { Project } from "../../../api/useProjects";
import type { Analysis } from "../../../api/Analysis";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import type { Source } from "../../../api/useSources";
import type { NonEmptyArray } from "diagnostics-lib/array";
import type { SelectedQuantity } from "../useSelectedQuantities";
import type { MeasuresChartType } from "./MeasuresChartType";
import { useGroupUnitState } from "./GroupUnitInput";
import MeasuresLegend from "../Chart/Legend/MeasuresLegend";
import CommonLegend from "../Chart/Legend/CommonLegend";
import { useDisplayInvalidPoints } from "../Analysis/ToggleDisplayInvalidPoints";
import { useQuantityNoDataIntervals } from "../useNoDataIntervals";
import { useCanUserDoOnProject } from "../../canUserDo";
import { useProjectPeriod } from "../useProjectPeriod";
import useSeries from "./useSeries";
import { usePublicationFlagDisplay } from "../Analysis/TogglePublicationDisplay";
import { useSettings } from "../../../api/useSettings";

interface Props {
  title: React.JSX.Element;
  subtitle: React.JSX.Element;
  legendLeft?: React.ReactNode;

  project: Project;
  analyses: Analysis[];
  activeAnalyses: ActiveAnalysis[];
  source: Source;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  canUpdateQuantityValues: boolean;
  selectedChartOption: MeasuresChartType;
  allAnalysesMode: boolean;
}

const MeasureHighcharts = ({
  title,
  subtitle,
  legendLeft,
  project,
  analyses,
  activeAnalyses,
  source,
  selectedQuantities,
  canUpdateQuantityValues,
  selectedChartOption,
  allAnalysesMode,
}: Props) => {
  const settings = useSettings();
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const actionAllowed = canUserWriteProject;

  const [axisGroupedByUnit] = useGroupUnitState({
    scope: project.id,
  });
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const [publicationFlagDisplay] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const [period] = useProjectPeriod(
    allAnalysesMode
      ? {
          source,
          project,
          boundaries: "analyses",
        }
      : {
          source,
          project,
          boundaries: "active-analyses",
          analyses: activeAnalyses,
        },
  );

  const chartRef = React.useRef<Highcharts.Chart>(null);

  const series = useSeries(
    project,
    analyses,
    activeAnalyses,
    selectedQuantities,
    selectedQuantities.length > 1 ? axisGroupedByUnit : false,
    actionAllowed,
    selectedChartOption,
    allAnalysesMode,
    publicationFlagDisplay,
  );

  const options = useOptions({
    chartRef,
    selectedQuantities,
    series,
    analyses,
    activeAnalyses,
    project,
    source,
    axisGroupedByUnit: selectedQuantities.length > 1 ? axisGroupedByUnit : false,
    multiColor: true,
    timeZone: project.timezone,
    canUpdateQuantityValues,
    period,
    selectedChartOption,
    allAnalysesMode,
  });

  const noDataIntervals = useQuantityNoDataIntervals(
    activeAnalyses,
    selectedQuantities,
    selectedChartOption,
    project.id,
  );

  const legend = useMemo(
    () => (
      <>
        <MeasuresLegend
          chartRef={chartRef}
          selectedChartOption={selectedChartOption}
          selectedQuantities={selectedQuantities}
          series={series}
          period={period}
          displayInvalidPoints={displayInvalidPoints}
        />
        <CommonLegend
          chartRef={chartRef}
          analyses={analyses}
          series={series}
          period={period}
          noDataIntervals={noDataIntervals}
          allAnalysesMode={allAnalysesMode}
          chartType="measures"
          projectId={project.id}
        />
      </>
    ),
    [
      selectedChartOption,
      selectedQuantities,
      series,
      period,
      analyses,
      allAnalysesMode,
      displayInvalidPoints,
      noDataIntervals,
      project.id,
    ],
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legendLeft={legendLeft}
      legend={legend}
      className={cx(measureChartCard, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <HighchartsComponent
        name="Measures"
        ref={chartRef}
        options={options}
        selectablePoints
        normalizeNavigatorSeries
        timezone={project.timezone}
        range={!allAnalysesMode}
      />
    </ChartCard>
  );
};

export default MeasureHighcharts;

//#region CSS
const measureChartCard = css`
  min-height: 600px;
  row-gap: ${spacings.m};
`;
//#endregion CSS
